import type { CSSProperties, MouseEvent, PointerEvent, TouchEvent } from 'react'
import { flexRender, type Header } from '@tanstack/react-table'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { TableColumnMeta } from '../columnAdapter'
import { TableHeaderIcon } from './TableHeaderIcon'
import * as s from '../Table.css'

export interface SortableHeaderCellProps<TData> {
  header: Header<TData, unknown>
  className: string
  /** Show the ⠿ grip (layout slot stays reserved). */
  showReorderHandle?: boolean
  /** When true, the grip is draggable. */
  enableReorder?: boolean
  isTableResizing?: boolean
  /** Disable sort, resize, and reorder (e.g. while fetching). */
  interactionDisabled?: boolean
  /** Pause column drag while the user clicks to sort (grip stays visible). */
  onSortPointerDown?: () => void
}

export function SortableHeaderCell<TData>({
  header,
  className,
  showReorderHandle = true,
  enableReorder = true,
  isTableResizing = false,
  interactionDisabled = false,
  onSortPointerDown,
}: SortableHeaderCellProps<TData>) {
  const column = header.column
  const meta = column.columnDef.meta as TableColumnMeta<unknown> | undefined
  const columnLabel =
    meta?.columnLabel ??
    (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)
  const headerLabelIcon = meta?.headerLabelIcon
  const canSort = column.getCanSort()
  const sorted = column.getIsSorted()
  const isResizing = column.getIsResizing()

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    disabled: !enableReorder,
    animateLayoutChanges: () => false,
  })

  const style: CSSProperties = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.35 : 1,
  }

  const handleResizeStart = (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    if (interactionDisabled) return
    e.stopPropagation()
    e.preventDefault()
    header.getResizeHandler()(e)
  }

  const isSortTarget = (target: EventTarget | null) => {
    const el = target as HTMLElement | null
    return Boolean(
      el?.closest(`.${s.thResizeHandle}, .${s.thDragHandle}`),
    )
  }

  const handleHeaderPointerDown = (e: PointerEvent<HTMLTableCellElement>) => {
    if (interactionDisabled || isTableResizing || isResizing) return
    if (isSortTarget(e.target)) return
    if (canSort) onSortPointerDown?.()
  }

  const handleHeaderClick = (e: MouseEvent<HTMLTableCellElement>) => {
    if (interactionDisabled || isTableResizing || isResizing) return
    if (isSortTarget(e.target)) return
    const handler = column.getToggleSortingHandler()
    if (canSort && handler) handler(e)
  }

  return (
    <th
      ref={setNodeRef}
      data-column-id={column.id}
      style={style}
      className={`${className}${canSort && !isTableResizing && !interactionDisabled ? ` ${s.thSortable}` : ''}${isDragging ? ` ${s.thDragging}` : ''}${isResizing ? ` ${s.thColumnResizing}` : ''}`}
      onPointerDown={handleHeaderPointerDown}
      onClick={handleHeaderClick}
    >
      <span className={s.thInner}>
        {showReorderHandle && (
          <span
            className={`${s.thDragHandle}${!enableReorder ? ` ${s.thDragHandleInactive}` : ''}`}
            aria-hidden
            onClick={(e) => e.stopPropagation()}
            {...(enableReorder ? { ...attributes, ...listeners } : {})}
          >
            ⠿
          </span>
        )}
        {headerLabelIcon ? (
          <TableHeaderIcon name={headerLabelIcon} label={columnLabel} />
        ) : (
          flexRender(column.columnDef.header, header.getContext())
        )}
        {canSort && (
          <span className={`${s.sortCaret}${sorted ? ` ${s.sortCaretActive}` : ''}`}>
            {sorted === 'asc' ? '↑' : sorted === 'desc' ? '↓' : '↕'}
          </span>
        )}
      </span>
      {column.getCanResize() && (
        <div
          className={`${s.thResizeHandle}${isResizing ? ` ${s.thResizeHandleActive}` : ''}`}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Resize ${columnLabel} column`}
        />
      )}
    </th>
  )
}
